import { css, jsx } from '@emotion/core';
import { Form, Icon, Select, Skeleton } from 'antd';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import Error from '@/pages/_error';
import BulletinsList from '@/src/advocacy/components/state/BulletinsList';
import ProvisionsTable from '@/src/advocacy/components/state/ProvisionsTable';
import { replaceSpaceAndBr, stInnerContentStyle } from '@/src/advocacy/utils';
import contentStyle from '@/src/cms/contentStyle';
import { generateHTMLString } from '@/src/pdf/templates/environmentReport';
import { convertHtmlToPdf } from '@/src/pdf/utils';
import Button from '@/src/shared/components/Button';
import Container from '@/src/shared/components/Container';
import Head from '@/src/shared/components/Head';
import Heading from '@/src/shared/components/Heading';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import Link from '@/src/shared/components/Link';
import Sanitize from '@/src/shared/components/Sanitize';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { checkAccess, COMPLIANCE_ACCESS } from '@/src/shared/constants';
import { bp } from '@/styles';
import request from '@/utils/axios';
import states from '@/utils/states';

const style = {
  select: css`
    max-width: 500px;
    margin-bottom: 4px;
    min-width: 300px;
    font-size: 16px;
    @media ${bp.md} {
      font-size: inherit;
    }
  `,
  screenOnly: css`
    @media print {
      display: none;
    }
  `,
  amicusCase: css`
    margin-bottom: 16px;
  `,
  pagebreak: css`
    page-break-before: always;
  `,
  heroInnerContainer: css`
    @media ${bp.md} {
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
    }
  `,
  stateForm: css`
    margin-bottom: 16px;
    .ant-form-item {
      margin-bottom: 0 !important;
    }
    @media ${bp.md} {
      margin-bottom: 0;
      flex: 1;
    }
  `,
  buttonGroup: css`
    @media ${bp.md} {
      margin-left: auto;
    }
  `,
};

const routeBase = '/advocacy/state/environment-report';

const LegalLandscape = props => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [StateSnapshots, setStateSnapshots] = useState([]);
  const [StateProvisions, setStateProvisions] = useState([]);
  const [LitigationBulletins, setLitigationBulletins] = useState([]);
  const [AmicusCases, setAmicusCases] = useState([]);
  const [Disclaimer, setDisclaimer] = useState('');
  const [pdfExportStatus, setPdfExportStatus] = useState(false);

  const { statecode, isLoggedIn, role, userinfo } = props;
  const _statecode = statecode || router.query.slug;

  function setData(data) {
    setStateSnapshots(data.StateSnapshots);
    setStateProvisions(data.StateProvisions);
    setLitigationBulletins(data.LitigationBulletins);
    setAmicusCases(data.AmicusCases);
    setDisclaimer(data.Disclaimer);
  }

  useEffect(() => {
    (async function mounted() {
      if (!_statecode) {
        if (router.route === '/advocacy/state/environment-report') {
          setPageLoaded(true);
        }
        return;
      }
      try {
        const response = await request({
          url: 'statesnapshot/environmentreport',
          params: { stateCode: _statecode },
          triggerLoad: !pageLoaded,
        });
        window.scrollTo(0, 0);
        const { data } = response;
        setData(data);
        setLoading(false);
        setPageLoaded(true);
      } catch (error) {
        setLoading(false);
      }
    })();
  }, [_statecode]);

  async function handleSelectChange(value) {
    setLoading(true);
    try {
      const response = await request({
        url: 'statesnapshot/environmentreport',
        params: { stateCode: value },
      });
      const { data } = response;
      setData(data);
      router.replace(`${routeBase}/[slug]`, `${routeBase}/${value.toUpperCase()}`, {
        shallow: true,
      });
      setTimeout(() => {
        window.scrollTo(0, 0);
        setLoading(false);
      }, 100);
    } catch (error) {
      console.log(error);
      setTimeout(() => setLoading(false), 100);
    }
  }

  async function handleExportPdf() {
    setPdfExportStatus(true);
    const fileName = `State Environment Report - ${_statecode}.pdf`;
    const html = generateHTMLString({
      StateSnapshots,
      StateProvisions,
      LitigationBulletins,
      AmicusCases,
      Disclaimer,
    });
    await convertHtmlToPdf({ html, fileName }, setPdfExportStatus);
  }

  const hasAccess = checkAccess(COMPLIANCE_ACCESS.COMPLIANCE, role);
  const isAuthorized = userinfo.PersonID && Array.isArray(role) && hasAccess;
  const errorCode = isLoggedIn && !hasAccess ? 401.4 : 401;

  if (!isLoggedIn || !isAuthorized) return <Error status={errorCode} />;

  const pageTitle = 'State Environment Report';

  return (
    <Layout>
      <Head title={pageTitle} />
      <Hero title={pageTitle}>
        <p>
          The APCIA State Environment Report is your one-stop resource for up-to-date information on
          a particular state. Use the drop down menu below to select a state and view the latest
          legal, regulatory, and legislative information, as well as market profile data.
        </p>
        <div css={style.heroInnerContainer}>
          <Form theme="white" css={[style.screenOnly, style.stateForm]}>
            <Form.Item label="Display for" colon>
              <Select
                disabled={!isLoggedIn}
                defaultValue={_statecode}
                onChange={handleSelectChange}
                css={style.select}
                placeholder="Select State"
                optionFilterProp="children"
                showSearch
              >
                {states.map(state => (
                  <Select.Option key={state.StateCode} value={state.StateCode}>
                    {state.StateDescription}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          <Button.Group css={[style.screenOnly, style.buttonGroup]}>
            <Button onClickPrint>
              <Icon type="printer" theme="filled" /> Print
            </Button>
            <Button onClickExportPDF={handleExportPdf} disabled={loading || pdfExportStatus}>
              <Icon type="file-pdf" theme="filled" /> Export PDF
            </Button>
          </Button.Group>
        </div>
      </Hero>
      {loading ? (
        <Section bgColor="white">
          <Container>{!!_statecode && <Skeleton active paragraph={{ rows: 12 }} />}</Container>
        </Section>
      ) : (
        <>
          <Section bgColor="gray">
            <Container>
              <Sanitize css={[contentStyle, stInnerContentStyle]}>
                {StateSnapshots.map(
                  tab =>
                    `<h2 class="st-content-heading__inner">${
                      tab.FieldLabel
                    }</h2>${replaceSpaceAndBr(tab.FieldValue)}`
                ).join('<div class="pagebreak"></div>')}
              </Sanitize>
            </Container>
          </Section>
          <div css={style.pagebreak} />
          <Section bgColor="white">
            <Container>
              <Heading
                type={2}
                weight="bolder"
                theme="primaryGray"
                css={css`
                  margin-bottom: 24px;
                `}
              >
                Selected State Provisions
              </Heading>
              <ProvisionsTable provisions={StateProvisions} />
            </Container>
          </Section>
          <div css={style.pagebreak} />
          <Section bgColor="gray">
            <Container>
              <Heading
                type={2}
                weight="bolder"
                theme="primaryGray"
                css={css`
                  margin-bottom: 24px;
                `}
              >
                Litigation Bulletins
              </Heading>
              <BulletinsList bulletins={LitigationBulletins} />
            </Container>
          </Section>
          <div css={style.pagebreak} />
          <Section bgColor="white">
            <Container>
              <Heading
                type={2}
                weight="bolder"
                theme="primaryGray"
                css={css`
                  margin-bottom: 24px;
                `}
              >
                Open Amicus Cases
              </Heading>
              {AmicusCases.map(amicusCase => (
                <div key={amicusCase.LegalCaseId} css={style.amicusCase}>
                  <Link href={`/advocacy/legal-judicial-advocacy/amicus/${amicusCase.LegalCaseId}`}>
                    {amicusCase.CaseName}
                  </Link>
                  <p>{amicusCase.IssueDescription}</p>
                </div>
              ))}
            </Container>
          </Section>
          <div css={style.pagebreak} />
          <Section bgColor="gray">
            <Container>
              <p>
                <strong>{Disclaimer}</strong>
              </p>
            </Container>
          </Section>
          <WebLogImage browserPath title="State Environment Report" sitePageId={_statecode} />
        </>
      )}
    </Layout>
  );
};

export async function getStaticPaths() {
  return {
    paths: states.map(s => ({ params: { slug: s.StateCode } })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  return {
    props: {
      statecode: params.slug || 0,
    },
  };
}

export default connect(
  state => {
    const { auth } = state;
    const { isLoggedIn, role, userinfo } = auth;
    return { isLoggedIn, role, userinfo };
  },
  null
)(LegalLandscape);
